import { Component, NgZone, ViewChild } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';

import pluralize from 'pluralize';
import { MenuItem } from 'primeng/api';
import { Listbox } from 'primeng/listbox';
import { combineLatest, debounceTime, fromEvent, map, startWith, take } from 'rxjs';

import { LoadingState } from 'src/app/utilities/state.util';

import { ColorSchemeService } from '../../color-scheme.service';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { LibModule } from '../../lib.ts/lib.module';
import { createConfigSelector } from '../../state/branding.store';
import { SharedModule } from '../shared.module';

import { CancelAndCloseDialogActions } from './cancel-and-close-dialog.actions';
import { cancelAndCloseDialogFeature } from './cancel-and-close-dialog.reducer';
import {
  selectBookedEvents,
  selectCancelAndCloseBlocker,
  selectCancelAndCloseJob,
  selectCancelAndClosePendingTransactions,
} from './cancel-and-close-dialog.selectors';

@Component({
  selector: 'app-cancel-and-close-dialog',
  standalone: true,
  imports: [
    SharedModule,
    LibModule,
    FreyaCommonModule,
  ],
  templateUrl: './cancel-and-close-dialog.component.html',
  styleUrls: ['./cancel-and-close-dialog.component.scss', '../../job/colors.scss'],
})
export class CancelAndCloseDialogComponent {

  constructor(
    public store: Store,
    public colorScheme: ColorSchemeService,
    private ngZone: NgZone,
  ) {}

  private lb?: Listbox;

  @ViewChild(Listbox) set closureListbox(input: Listbox | undefined) {

    if (!input) {
      this.lb = undefined;
      return;
    }

    if (this.lb) return;

    this.lb = input;

    this.ngZone.onStable.pipe(take(1)).subscribe(() => this.focusListboxFilterInput());
  }

  public actions = CancelAndCloseDialogActions;

  private _selectViewModel = createSelector({
    jobId: cancelAndCloseDialogFeature.selectJobId,
    visible: createSelector(
      cancelAndCloseDialogFeature.selectJobId,
      (jobId) => jobId != null,
    ),
    view: createSelector(
      selectCancelAndCloseJob,
      cancelAndCloseDialogFeature.selectView,
      (job, view) => !job ? 'loading' : view,
    ),
    header: createSelector(
      cancelAndCloseDialogFeature.selectView,
      (view) => view === 'cancel' ? 'Cancel Events?' : 'Close Job?',
    ),
    cancelPrompt: createSelector(
      cancelAndCloseDialogFeature.selectTrigger,
      (trigger) => trigger === 'cancel'
        ? 'Select events to cancel.'
        : 'This job has pending events. To close it, you need to cancel the following events.',
    ),
    closePrompt: createSelector(
      cancelAndCloseDialogFeature.selectTrigger,
      (trigger) => trigger === 'cancel'
        ? 'If you also want to close the job, please select a reason.'
        : 'Select a reason.',
    ),
    bookedEvents: selectBookedEvents,
    closedReason: cancelAndCloseDialogFeature.selectClosedReason,
    closureReasons: createSelector(
      createConfigSelector('JOBS_CLOSEDREASONS'),
      selectCancelAndCloseJob,
      (reasons, job): MenuItem[] => {

        if (!reasons) return [];

        const stage = job?.stage || 'lead';

        return reasons
          .filter((r) => r.stages?.length === 0 || r.stages.includes(stage))
          .map((r) => ({
            label: r.title,
            title: r.title,
            id: r.id,
          }));
      },
    ),
    closeBlocker: createSelector(
      selectCancelAndCloseBlocker,
      selectCancelAndClosePendingTransactions,
      (blocker, { payLinkCount }) => {
        if (blocker === 'pendingTransactions') {
          return 'This job has pending transactions. Resolve them in the job\'s financials before closing it.';
        }

        if (blocker === 'payLinkPermission') {
          return payLinkCount === 1
            ? 'This job has an open pay link, and you don\'t have permission to close it.'
            : 'This job has open pay links, and you don\'t have permission to close them.';
        }

        return undefined;
      },
    ),
    payLinkWarning: createSelector(
      selectCancelAndClosePendingTransactions,
      ({ payLinkCount }) => {
        if (payLinkCount === 0) return undefined;

        return payLinkCount === 1
          ? 'The open pay link on this job will be closed. The customer will no longer be able to pay it.'
          : `The ${payLinkCount} open pay links on this job will be closed. The customer will no longer be able to pay them.`;
      },
    ),
    closeJobButton: createSelector(
      cancelAndCloseDialogFeature.selectClosedReason,
      cancelAndCloseDialogFeature.selectCancelAndCloseCall,
      cancelAndCloseDialogFeature.selectCancelEventsCall,
      selectCancelAndCloseBlocker,
      (closedReason, cancelAndCloseCall, cancelEventsCall, closeBlocker) => {
        if (cancelAndCloseCall === LoadingState.MUTATING) {
          return {
            label: 'Closing...',
            disabled: true,
            icon: 'pi pi-spin pi-spinner',
          };
        }

        return {
          label: 'Close job',
          disabled: !closedReason || cancelEventsCall === LoadingState.MUTATING || Boolean(closeBlocker),
          icon: undefined as string | undefined,
        };
      },
    ),
    cancelEventsOnlyButton: createSelector(
      cancelAndCloseDialogFeature.selectCancelEventsCall,
      cancelAndCloseDialogFeature.selectCancelAndCloseCall,
      selectBookedEvents,
      (cancelEventsCall, cancelAndCloseCall, events) => {

        if (cancelEventsCall === LoadingState.MUTATING) {
          return {
            label: 'Canceling...',
            disabled: true,
            icon: 'pi pi-spin pi-spinner',
          };
        }

        const eventCount = events.filter((e) => e.selected).length;

        return {
          label: `Keep job open, just cancel ${pluralize('event', eventCount)}`,
          disabled: cancelAndCloseCall === LoadingState.MUTATING,
          icon: undefined as string | undefined,
        };
      },
    ),
    continueButton: createSelector(
      cancelAndCloseDialogFeature.selectTrigger,
      cancelAndCloseDialogFeature.selectCancelEventsCall,
      selectBookedEvents,
      (trigger, cancelEventsCall, events) => {
        const isMutating = cancelEventsCall === LoadingState.MUTATING;

        if (isMutating) {
          return {
            label: 'Canceling...',
            disabled: true,
            command: 'cancel',
            icon: 'pi pi-spin pi-spinner',
          } as const;
        }

        const total = events.length;
        const checked = events.filter((e) => e.selected).length;
        const allChecked = total > 0 && checked === total;
        const someChecked = checked > 0;

        const isCancel = trigger === 'cancel' && someChecked && !allChecked;

        return {
          label: isCancel ? 'Cancel' : 'Continue',
          disabled: trigger === 'close' ? !allChecked : !someChecked,
          command: isCancel ? 'cancel' : 'continue',
          icon: isCancel ? 'pi pi-ban' : 'pi pi-arrow-right',
        } as const;
      },
    ),
  });

  public viewModel$ = combineLatest([
    this.store.select(this._selectViewModel),
    fromEvent(window, 'resize').pipe(
      map((event: Event) => (event.target as Window).innerWidth),
      startWith(window.innerWidth),
      debounceTime(150),
    ),
  ]).pipe(map(([ viewModel, windowWidth ]) => {

    const maxWidthPx = viewModel.view === 'close' ? 600 : 550;

    let width = `${maxWidthPx}px`;

    const maxViewportRatio = 0.93;

    const responsiveWidth = windowWidth * maxViewportRatio;

    if (responsiveWidth < maxWidthPx) {
      width = `${Math.round(maxViewportRatio * 100)}vw`;
    }

    return {
      ...viewModel,
      style: {
        width,
      },
    };
  }));

  private focusListboxFilterInput() {

    const host: HTMLElement | undefined = this.lb?.el?.nativeElement;

    if (!host) return;

    const filterInput: HTMLInputElement = host.querySelector('input.p-listbox-filter, input[role="searchbox"]');

    if (!filterInput) return;

    filterInput.focus();
    filterInput.select?.();
  }

}
